import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { initCatEyeTracker, cleanupCatEyeTracker } from '../utils/catRenderer';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [theme, setTheme] = useState('dark');
  const [availableThemes, setAvailableThemes] = useState([]);
  const [activeCursor, setActiveCursor] = useState(null);
  const [loading, setLoading] = useState(true);

  // Theme CSS imports
  const themeStyles = {
    'cat-premium': () => import('../styles/cat-premium.css'),
    'cat-premium-dark': () => import('../styles/cat-premium-dark.css')
  };

  // Apply cursor
  const applyCursor = (cursorId) => {
    setActiveCursor(cursorId);
    // If no cursor, make sure old style tag is removed
    const oldStyle = document.getElementById('custom-cursor-style');
    if (oldStyle) oldStyle.remove();
    document.documentElement.style.cursor = 'auto';
    document.body.style.cursor = 'auto';
  };

  // Load user's active theme and available themes
  useEffect(() => {
    // Don't apply themes in preview mode
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview')) {
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Fetch themes
        const themesResponse = await axios.get('/api/shop/themes', {
          headers: { Authorization: `Bearer ${token}` }
        });

        const { themes, activeTheme } = themesResponse.data.data;
        setAvailableThemes(themes);
        applyTheme(activeTheme || 'dark');
        
        // Fetch cosmetics to get active cursor
        const cosmeticsResponse = await axios.get('/api/shop/cosmetics', {
          headers: { Authorization: `Bearer ${token}` }
        });

        const { equipped } = cosmeticsResponse.data.data;
        if (equipped && equipped.cursor) {
          applyCursor(equipped.cursor);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Fallback to local storage
        const savedTheme = localStorage.getItem('theme') || 'dark';
        applyTheme(savedTheme);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchUserData();
    } else {
      // Not authenticated, use local storage
      const savedTheme = localStorage.getItem('theme') || 'dark';
      applyTheme(savedTheme);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const applyTheme = (themeName) => {
    // Don't apply themes in preview mode
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview')) {
      return;
    }

    // Cleanup cat eye tracker if switching away from cat theme
    if ((theme === 'cat-premium' || theme === 'cat-premium-dark') && 
        (themeName !== 'cat-premium' && themeName !== 'cat-premium-dark')) {
      cleanupCatEyeTracker();
    }

    setTheme(themeName);
    
    // Apply new theme
    if (themeName === 'light') {
      document.documentElement.removeAttribute('data-theme');
      document.body.classList.remove('dark-mode');
      document.body.classList.add('light-mode');
    } else if (themeName === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.body.classList.remove('light-mode');
      document.body.classList.add('dark-mode');
    } else {
      // Halloween theme or custom themes (cat-premium, etc.)
      document.body.classList.remove('light-mode');
      
      // Cat premium theme uses light mode styling as base
      if (themeName === 'cat-premium') {
        document.body.classList.add('light-mode');
      } else {
        document.body.classList.add('dark-mode');
      }
      
      document.documentElement.setAttribute('data-theme', themeName);
      
      // Load the corresponding CSS file
      if (themeStyles[themeName]) {
        themeStyles[themeName]()
          .then(() => {
            // Initialize cat eye tracker after CSS is loaded
            if (themeName === 'cat-premium' || themeName === 'cat-premium-dark') {
              // Small delay to ensure DOM is ready
              setTimeout(() => {
                initCatEyeTracker();
              }, 500);
            }
          })
          .catch(err => {
            console.error(`Failed to load theme ${themeName}:`, err);
          });
      }
    }

    // Save to localStorage
    localStorage.setItem('theme', themeName);
  };

  const changeTheme = async (newTheme) => {
    if (!isAuthenticated) {
      // Just change locally
      applyTheme(newTheme);
      return;
    }

    // For authenticated users, update on server
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/shop/theme/activate', 
        { themeId: newTheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      applyTheme(newTheme);
    } catch (error) {
      console.error('Error changing theme:', error);
      alert(error.response?.data?.message || 'Failed to change theme');
      throw error;
    }
  };

  const toggleTheme = () => {
    // Toggle between light and dark for non-authenticated or simple toggle
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    changeTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      availableThemes,
      changeTheme,
      toggleTheme,
      loading,
      isDarkMode: theme !== 'light',
      activeCursor,
      applyCursor
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
